export type InputKind = 'orderId' | 'trackingNumber';

export type ParseResult =
    | { ok: true; kind: InputKind; orderIds: number[]; trackingNumbers: string[] }
    | { ok: false; errorMessageKey: string };

const hasChineseComma = (value: string) => value.includes('，');

/**
 * 解析文本输入：自动判断是纯数字（orderId）还是含非数字字符（trackingNumber）。
 * 同批输入必须全是同一种（不混用）。
 */
export function parseIdsOrTrackingNumbersFromText(
    value: string,
    kind: InputKind,
    maxCount: number,
): ParseResult {
    const text = (value ?? '').trim();
    if (!text) {
        return { ok: false, errorMessageKey: 'supportTools.esTools.errors.empty' };
    }
    if (hasChineseComma(text)) {
        return { ok: false, errorMessageKey: 'supportTools.esTools.errors.chineseCommaNotAllowed' };
    }
    const tokens = text
        .split(/[\n,\s]+/g)
        .map((t) => t.trim())
        .filter(Boolean);
    if (tokens.length === 0) {
        return { ok: false, errorMessageKey: 'supportTools.esTools.errors.empty' };
    }
    if (tokens.length > maxCount) {
        return { ok: false, errorMessageKey: 'supportTools.esTools.errors.exceedLimit' };
    }

    const seen = new Set<string>();
    const dedupe = <T,>(items: T[], keyOf: (i: T) => string): T[] => {
        const out: T[] = [];
        for (const i of items) {
            const k = keyOf(i);
            if (!seen.has(k)) {
                seen.add(k);
                out.push(i);
            }
        }
        return out;
    };

    if (kind === 'orderId') {
        const ids: number[] = [];
        for (const t of tokens) {
            if (!/^\d+$/.test(t)) {
                return { ok: false, errorMessageKey: 'supportTools.esTools.errors.invalidOrderId' };
            }
            const n = Number(t);
            if (!Number.isSafeInteger(n) || n <= 0) {
                return { ok: false, errorMessageKey: 'supportTools.esTools.errors.invalidOrderId' };
            }
            ids.push(n);
        }
        return { ok: true, kind, orderIds: dedupe(ids, (i) => String(i)), trackingNumbers: [] };
    }

    // trackingNumber：不硬性约束格式（各渠道运单号格式差异大），只禁空白
    const tns = dedupe(tokens, (t) => t);
    return { ok: true, kind, orderIds: [], trackingNumbers: tns };
}
